/**
 * Browser-specific guidance for lifting a blocked camera/microphone permission.
 *
 * A site-level block can only be cleared by the customer in browser chrome, so
 * this copy is the whole recovery path for `NotAllowedError`. It is shared by
 * the pre-call permission gate (TransitionScreen) and the in-call device-error
 * modal (MediaErrorPrompt) — one wording, one place to correct it.
 *
 * The instructions name the device that was actually blocked: browser settings
 * keep camera and microphone on separate pages, so sending a customer whose
 * microphone is blocked to the Camera page is sending them nowhere.
 */

import type { MediaRequest } from '../providers/types';

export type BrowserName = 'Chrome' | 'Safari' | 'Firefox' | 'Other';

export function getBrowserName(): BrowserName {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (ua.indexOf('Chrome') > -1 && ua.indexOf('Edg') === -1) return 'Chrome';
  if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) return 'Safari';
  if (ua.indexOf('Firefox') > -1) return 'Firefox';
  return 'Other';
}

/** Settings-page name for the blocked device, in each browser's own wording. */
function settingsSection(requested: MediaRequest): string {
  switch (requested) {
    case 'camera': return 'Camera';
    case 'microphone': return 'Microphone';
    case 'both': return 'Camera and Microphone';
  }
}

function deviceWords(requested: MediaRequest): string {
  switch (requested) {
    case 'camera': return 'camera';
    case 'microphone': return 'microphone';
    case 'both': return 'camera and microphone';
  }
}

export function getBrowserInstructions(
  requested: MediaRequest = 'both',
  browser: BrowserName = getBrowserName()
): string {
  const section = settingsSection(requested);
  switch (browser) {
    case 'Chrome':
      return `Click the settings icon at the start of the address bar, or go to Settings > Privacy > Site Settings > ${section}`;
    case 'Safari':
      return `Go to Safari > Settings > Websites > ${section} and select Allow`;
    case 'Firefox':
      return 'Click the lock icon in the address bar, then clear the blocked permission';
    default:
      return `Open your browser settings and allow ${deviceWords(requested)} access for this site`;
  }
}

/**
 * Where to lift a refusal made by the operating system rather than the browser
 * (Chrome: "Permission denied by system"). Browser settings cannot fix that one.
 */
export function getSystemInstructions(requested: MediaRequest = 'both'): string {
  const section = settingsSection(requested);
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.userAgent);
  return isMac
    ? `Open System Settings > Privacy & Security > ${section} and turn on your browser`
    : `Open your system privacy settings for ${deviceWords(requested)} and allow your browser`;
}
